import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { loadBedsByBuildingId, loadBoxesByBuildingId, loadOfficesByBuildingId, loadPatientCallers, loadRoomsByBuildingId, loadSectorsByBuildingId, loadTotems } from '../../../servicesHooks/Location/useLocationConfigService';
import { Establishment, PatientCaller, Totem } from '../../../interface/interface';
import { useDispatch } from 'react-redux';
import { ETypeOfNotification, setNotification } from 'medere-ui-framework';
import { getPreferredBuilding } from '../../../servicesHooks/ProfileDataForm/useProfileDataFormService';

interface LocationConfigContextProps {
    establishments: Establishment[];
    setEstablishments: React.Dispatch<React.SetStateAction<Establishment[]>>;
    activeEstablishments: Establishment[];
    /** Establecimiento preferido del usuario (Mi perfil o puesto de atención abierto): undefined mientras carga, null si no hay. */
    preferredBuildingId: number | null | undefined;
    /** Para mostrar: el establecimiento preferido primero. No usar sus índices para actualizar `establishments`. */
    orderedEstablishments: Establishment[];
    orderedActiveEstablishments: Establishment[];
    totems: Totem[];
    setTotems: React.Dispatch<React.SetStateAction<Totem[]>>;
    activeTab: number;
    setActiveTab: React.Dispatch<React.SetStateAction<number>>;
    setSectorsToEstablishments: (establishments: Establishment[]) => Promise<void>;
    setOfficesToEstablishments: () => Promise<void>;
    setBoxesToEstablishments: () => Promise<void>;
    setRoomsToEstablishments: () => Promise<void>;
    setBedsToEstablishments: () => Promise<void>;
    setCallersToEstablishments: (establishments: Establishment[]) => Promise<void>;
    setTotemList: () => Promise<void>;
    hasDuplicateAssociatedHospitalLocation: (e: PatientCaller | Totem) => boolean;
}

const LocationConfigContext = createContext<LocationConfigContextProps | undefined>(undefined);

/** El establecimiento preferido del usuario primero; el resto conserva el orden del back. */
function putPreferredBuildingFirst(list: Establishment[], preferredBuildingId: number | null | undefined): Establishment[] {
    const preferredIndex = preferredBuildingId == null ? -1 : list.findIndex(est => est.buildingid === preferredBuildingId);
    if (preferredIndex <= 0) return list;
    return [list[preferredIndex], ...list.slice(0, preferredIndex), ...list.slice(preferredIndex + 1)];
}

export const useLocationConfigContext = () => { //Se debe invocar este hook para acceder a los elementos del contexto en las vistas
    const context = useContext(LocationConfigContext);
    if (!context) throw new Error("useLocationConfig debe usarse dentro de LocationConfigProvider");
    return context;
};

export const LocationConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => { //Provee el contexto, debe envolver aquello en donde se quiere usar
    const [establishments, setEstablishments] = useState<Establishment[]>([]);
    const [totems, setTotems] = useState<Totem[]>([])
    const [activeTab, setActiveTab] = useState(0);
    const [preferredBuildingId, setPreferredBuildingId] = useState<number | null | undefined>(undefined);

    const dispatch = useDispatch()

    useEffect(() => {
        getPreferredBuilding()
            .then((building) => setPreferredBuildingId(building?.id ?? null))
            .catch((error) => {
                // Sin establecimiento preferido se ve todo como antes; no amerita avisar al usuario.
                console.error("Error al cargar el establecimiento preferido:", error);
                setPreferredBuildingId(null);
            });
    }, []);

    const mergeEstablishmentsByBuilding = <K extends keyof Establishment>(key: K, valuesByBuildingId: Map<number, Establishment[K]>) => {
        setEstablishments((prev) =>
            (prev ?? []).map((est) => {
                if (!valuesByBuildingId.has(est.buildingid)) return est;
                return {
                    ...est,
                    [key]: valuesByBuildingId.get(est.buildingid),
                } as Establishment;
            })
        );
    };

    const setSectorsToEstablishments = async (targetEstablishments: Establishment[]) => { //Obtiene los sectores de cada establecimiento
        if (!targetEstablishments || targetEstablishments.length === 0) {
            return;
        }

        const sectorsByBuilding = new Map<number, Establishment["sectors"]>();
        await Promise.all(
            targetEstablishments.map(async (est) => {
                try {
                    const sectors = await loadSectorsByBuildingId({ buildingid: est.buildingid });
                    sectorsByBuilding.set(est.buildingid, sectors ?? []);
                } catch (error) {
                    console.error(`Error cargando sectores para establecimiento ${est.buildingid}`, error);
                    dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudieron cargar los sectores para el establecimiento ${est.buildingid}`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                    sectorsByBuilding.set(est.buildingid, []);
                }
            })
        );

        mergeEstablishmentsByBuilding("sectors", sectorsByBuilding);
    };

    const setOfficesToEstablishments = async () => {
        if (establishments && establishments.length > 0) {
            const officesByBuilding = new Map<number, Establishment["offices"]>();
            await Promise.all(
                establishments.map(async (est) => {
                    try {
                        const offices = await loadOfficesByBuildingId({ buildingid: est.buildingid });
                        officesByBuilding.set(est.buildingid, offices ?? []);
                    } catch (error) {
                        console.error(`Error cargando consultorios para ${est.buildingid}`, error);
                        dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudieron cargar los consultorios para el establecimiento ${est.buildingid}`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                        officesByBuilding.set(est.buildingid, []);
                    }
                })
            );
            mergeEstablishmentsByBuilding("offices", officesByBuilding);
        }
    };

    const setBoxesToEstablishments = async () => {
        if (establishments && establishments.length > 0) {
            const boxesByBuilding = new Map<number, Establishment["boxes"]>();
            await Promise.all(
                establishments.map(async (est) => {
                    try {
                        const boxes = await loadBoxesByBuildingId({ buildingid: est.buildingid });
                        boxesByBuilding.set(est.buildingid, boxes ?? []);
                    } catch (error) {
                        console.error(`Error cargando consultorios para ${est.buildingid}`, error);
                        dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudieron cargar los puestos para el establecimiento ${est.buildingid}`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                        boxesByBuilding.set(est.buildingid, []);
                    }
                })
            );

            mergeEstablishmentsByBuilding("boxes", boxesByBuilding);
        }
    };

    const setRoomsToEstablishments = async () => {
        if (establishments && establishments.length > 0) {
            const roomsByBuilding = new Map<number, Establishment["rooms"]>();
            await Promise.all(
                establishments.map(async (est) => {
                    try {
                        const rooms = await loadRoomsByBuildingId({ buildingid: est.buildingid });
                        roomsByBuilding.set(est.buildingid, rooms ?? []);
                    } catch (error) {
                        console.error(`Error cargando habitaciones para ${est.buildingid}`, error);
                        dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudieron cargar las habitaciones para el establecimiento ${est.buildingid}`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                        roomsByBuilding.set(est.buildingid, []);
                    }
                })
            );

            mergeEstablishmentsByBuilding("rooms", roomsByBuilding);
        }
    };

    const setBedsToEstablishments = async () => {
        if (establishments && establishments.length > 0) {
            const bedsByBuilding = new Map<number, Establishment["beds"]>();
            await Promise.all(
                establishments.map(async (est) => {
                    try {
                        const beds = await loadBedsByBuildingId({ buildingid: est.buildingid });
                        bedsByBuilding.set(est.buildingid, beds ?? []);
                    } catch (error) {
                        console.error(`Error cargando camas para ${est.buildingid}`, error);
                        dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudieron cargar las camas para el establecimiento ${est.buildingid}`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                        bedsByBuilding.set(est.buildingid, []);
                    }
                })
            );

            mergeEstablishmentsByBuilding("beds", bedsByBuilding);
        }
    };

    const setCallersToEstablishments = async (targetEstablishments: Establishment[]) => {
        if (!targetEstablishments || targetEstablishments.length === 0) {
            return;
        }

        try {
            const allCallers = await loadPatientCallers();

            const callersByBuilding: Record<number, PatientCaller[]> = {}; //Donde guardo los callers que luego voy a asignar a los establecimientos

            if (allCallers && allCallers.length > 0)
                allCallers.forEach((caller: PatientCaller) => {
                    const buildingId = caller.locationdtolist[0]?.building; //Siempre debería haber al menos un box/consultorio del que sacar a que establecimiento pertenece el caller
                    if (buildingId != null) {
                        caller.establishment = buildingId
                        if (!callersByBuilding[buildingId]) {
                            callersByBuilding[buildingId] = [];
                        }
                        callersByBuilding[buildingId].push(caller);
                    }
                });

            const callersByEstablishment = new Map<number, Establishment["callers"]>(
                targetEstablishments.map((est) => [est.buildingid, callersByBuilding[est.buildingid] ?? []])
            );
            mergeEstablishmentsByBuilding("callers", callersByEstablishment);
        } catch (error) {
            console.error("Error al cargar los PatientCallers:", error);
            dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudieron cargar los llamadores`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        }
    };

    const setTotemList = async () => {
        try {
            const allTotems = await loadTotems();
            const updatedTotems = (allTotems ?? []).map((totem: Totem) => {
                const estId = totem.locationdtolist?.[0]?.building ?? null;
                const hwq = Boolean(totem.hasWaitingQueue);
                return {
                    ...totem,
                    hasWaitingQueue: hwq,
                    establishment: estId ?? null
                };
            });
            setTotems(updatedTotems);
        } catch (error) {
            console.error("Error al cargar los Totems:", error);
        }
    };

    const hasDuplicateAssociatedHospitalLocation = (e: PatientCaller | Totem) => {
        return Array.isArray(e.locationdtolist) && e.locationdtolist.reduce((acc, curr) => {
            const loc = curr.hospitallocation;
            acc.map[loc] = (acc.map[loc] || 0) + 1;
            if (acc.map[loc] === 2) acc.hasDuplicate = true;
            return acc;
        }, { map: {}, hasDuplicate: false }).hasDuplicate;
    }

    const activeEstablishments = useMemo(() => (establishments ?? []).filter(e => e.active === true), [establishments]);

    const orderedEstablishments = useMemo(
        () => putPreferredBuildingFirst(establishments ?? [], preferredBuildingId),
        [establishments, preferredBuildingId]
    );
    const orderedActiveEstablishments = useMemo(
        () => putPreferredBuildingFirst(activeEstablishments, preferredBuildingId),
        [activeEstablishments, preferredBuildingId]
    );

    return (
        <LocationConfigContext.Provider value={{ establishments, activeEstablishments, preferredBuildingId, orderedEstablishments, orderedActiveEstablishments, setEstablishments, totems, setTotems, activeTab, setActiveTab, setSectorsToEstablishments, setOfficesToEstablishments, setBoxesToEstablishments, setRoomsToEstablishments, setBedsToEstablishments, setCallersToEstablishments, setTotemList, hasDuplicateAssociatedHospitalLocation }}>
            {children}
        </LocationConfigContext.Provider>
    );
};
